import { JSONContent } from '@tiptap/core';
import Dexie, { Table } from 'dexie';

export interface CoreLessonPlanTags {
  grade?: number | string;
  subject?: string;
  topic?: string;
  customPrompt?: string;
  classStrength?: number;
  pageNumber?: string;
  exercisePageNumber?: string;
  gradeSubject?: number;
  revisionLp?: boolean;
  lpType?: string | null;
  created?: string;
  modified?: string;
  [key: string]: any;
}

export interface ICachedCoreLessonPlan {
  id: number;
  content: string | null;
  contentBilingual: string | null;
  tags: CoreLessonPlanTags;
  createdBy: number;
  status: 'ready' | 'pending';
  created: string;
  modified: string;
  cachedAt: number;
  lastAccessed?: number;
  lastViewedHistoryId?: number | null;
  completedAt?: string | null;
  rating?: number | null;
  feedback?: string | null;
}

export enum CoreLessonPlanQuestionPurpose {
  Fidelity = 'fidelity',
  MiniAssessment = 'mini_assessment',
}

export interface ICoreLessonPlanQuestion {
  id: number;
  coreLessonPlanId: number;
  text: string;
  purpose: CoreLessonPlanQuestionPurpose;
  order: number;
  cachedAt: number;
}

export interface ICoreLessonPlanOption {
  id: number;
  questionId: number;
  text: string;
  isCorrect: boolean;
  order: number;
}

export class CoreLessonPlanDB extends Dexie {
  coreLessonPlans!: Table<ICachedCoreLessonPlan>;
  coreLessonPlanQuestions!: Table<ICoreLessonPlanQuestion>;
  coreLessonPlanOptions!: Table<ICoreLessonPlanOption>;

  constructor() {
    super('CoreLessonPlanDB');
    this.version(2).stores({
      coreLessonPlans:
        'id, status, created, modified, cachedAt, lastAccessed, tags.grade, tags.subject',
      coreLessonPlanQuestions: 'id, coreLessonPlanId, purpose',
      coreLessonPlanOptions: 'id, questionId',
    });
    this.version(3).stores({
      coreLessonPlans:
        'id, status, created, modified, cachedAt, lastAccessed, completedAt, tags.grade, tags.subject',
      coreLessonPlanQuestions: 'id, coreLessonPlanId, purpose',
      coreLessonPlanOptions: 'id, questionId',
    });
    this.version(4).stores({
      coreLessonPlans:
        'id, status, created, modified, cachedAt, lastAccessed, completedAt, lastViewedHistoryId, tags.grade, tags.subject',
      coreLessonPlanQuestions: 'id, coreLessonPlanId, purpose',
      coreLessonPlanOptions: 'id, questionId',
    });
  }

  async cacheLessonPlan(
    lessonPlan: Omit<ICachedCoreLessonPlan, 'cachedAt'>,
  ): Promise<void> {
    const cachedPlan: ICachedCoreLessonPlan = {
      ...lessonPlan,
      cachedAt: Date.now(),
      lastAccessed: Date.now(),
    };

    await this.coreLessonPlans.put(cachedPlan);
  }

  async getCachedLessonPlans(): Promise<ICachedCoreLessonPlan[]> {
    return await this.coreLessonPlans.orderBy('created').reverse().toArray();
  }

  async getCachedLessonPlanById(
    id: number,
  ): Promise<ICachedCoreLessonPlan | undefined> {
    const plan = await this.coreLessonPlans.get(id);
    if (plan) {
      await this.coreLessonPlans.update(id, { lastAccessed: Date.now() });
    }
    return plan;
  }

  async saveLastViewedHistoryId(
    lessonPlanId: number,
    historyId: number | null,
  ): Promise<void> {
    await this.coreLessonPlans.update(lessonPlanId, {
      lastViewedHistoryId: historyId,
    });
  }

  async getLastViewedHistoryId(
    lessonPlanId: number,
  ): Promise<number | null | undefined> {
    const plan = await this.coreLessonPlans.get(lessonPlanId);
    return plan?.lastViewedHistoryId;
  }

  async getStorageStats(): Promise<{
    total: number;
    ready: number;
    pending: number;
  }> {
    const total = await this.coreLessonPlans.count();
    const ready = await this.coreLessonPlans
      .where('status')
      .equals('ready')
      .count();
    const pending = await this.coreLessonPlans
      .where('status')
      .equals('pending')
      .count();

    return { total, ready, pending };
  }

  async cacheQuestions(
    coreLpId: number,
    questions: Array<{
      id: number;
      text: string;
      purpose: CoreLessonPlanQuestionPurpose;
      order: number;
      options: Array<{
        id: number;
        text: string;
        isCorrect: boolean;
        order?: number;
      }>;
    }>,
  ): Promise<void> {
    const now = Date.now();

    // Cache questions
    const questionRecords: ICoreLessonPlanQuestion[] = questions.map((q) => ({
      id: q.id,
      coreLessonPlanId: coreLpId,
      text: q.text,
      purpose: q.purpose,
      order: q.order,
      cachedAt: now,
    }));
    await this.coreLessonPlanQuestions.bulkPut(questionRecords);

    // Cache options
    const optionRecords: ICoreLessonPlanOption[] = questions.flatMap((q) =>
      q.options.map((opt, optIndex) => ({
        id: opt.id,
        questionId: q.id,
        text: opt.text,
        isCorrect: opt.isCorrect,
        order: opt.order ?? optIndex,
      })),
    );
    await this.coreLessonPlanOptions.bulkPut(optionRecords);
  }

  async getQuestionsByCoreLessonPlan(
    coreLpId: number,
    purpose: CoreLessonPlanQuestionPurpose,
  ): Promise<
    (ICoreLessonPlanQuestion & { options: ICoreLessonPlanOption[] })[]
  > {
    const questions = await this.coreLessonPlanQuestions
      .where('coreLessonPlanId')
      .equals(coreLpId)
      .filter((q) => q.purpose === purpose)
      .toArray();

    if (questions.length === 0) return [];

    const questionIds = questions.map((q) => q.id);
    const options = await this.coreLessonPlanOptions
      .where('questionId')
      .anyOf(questionIds)
      .toArray();

    return questions.map((q) => ({
      ...q,
      options: options.filter((opt) => opt.questionId === q.id),
    }));
  }

  clearData() {
    return this.coreLessonPlans.clear();
  }

  private getStartOfWeekUTC(): Date {
    const now = new Date();
    const dayOfWeek = now.getUTCDay();
    const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const startOfWeek = new Date(
      Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate() - diff,
      ),
    );
    return startOfWeek;
  }

  async getCurrentWeekCoreLPCount(): Promise<number> {
    const startOfWeek = this.getStartOfWeekUTC().toISOString();

    return this.coreLessonPlans
      .where('created')
      .aboveOrEqual(startOfWeek)
      .count();
  }
}

export const coreLessonPlanDB = new CoreLessonPlanDB();
